import { Component } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { CountryCode } from 'libphonenumber-js';
import { filter } from 'rxjs';
import { GuestScreeningService } from './screening/guest-screening.service';
import { GuestService } from '../services/guest.service';
import { Logger } from '../../../infrastructure/logging/logger';
import { getPhoneNumberFormatHint, formatPhoneNumber, formatNationalPhoneNumber, validatePhoneNumber } from '../../../utilities/PhoneUtility';
import { GuestScreeningFormComponent } from './components/guest-screening-form/guest-screening-form.component';

@Component({
  selector: 'app-guest-screening',
  templateUrl: './guest-screening.component.html',
  styleUrls: ['./guest-screening.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    GuestScreeningFormComponent
  ],
  providers: [ GuestScreeningService ]
})
export class GuestScreeningComponent {

  guestForm!: FormGroup;

  constructor(
    private formBuilder: FormBuilder,
    private guestService: GuestService,
    private router: Router,
    public dateAdapter: DateAdapter<Date>,
    private logService: Logger,
    public guestScreeningService: GuestScreeningService
  ) { }

  ngOnInit() {
    this.guestScreeningService.error$.subscribe(error => {
      this.logService.handleError(error, 'An error occurred while screening guest', 'An error occurred while screening guest');
    });

    this.load();

    this.guestForm.valueChanges.subscribe(value => {
      this.guestScreeningService.updateFormState(value);
    });

    this.guestScreeningService.submitted$.subscribe(guestId => {
      this.navigateToDetailPage(guestId);
    });
  }

  async load() {
    this.setupForm();
    this.subscribeToCountryCodeChanges();
    this.guestScreeningService.load();
  }

  subscribeToCountryCodeChanges() {
    this.guestScreeningService.countryCode$.pipe(
      filter((countryCode: CountryCode | null) => countryCode !== null)
    ).subscribe(countryCode => {
      this.updatePhoneNumberWhenCountryCodeChanges();
      this.guestScreeningService.updatePhoneNumberFormat(getPhoneNumberFormatHint(countryCode as CountryCode));
    });
  }

  setupForm() {
    this.guestForm = this.formBuilder.group({
      organization: [''],
      firstName: ['', [Validators.required, Validators.pattern(/[\S]/)]],
      middleName: [{ value: '', disabled: false }, [Validators.pattern(/[\S]/)]],
      lastName: ['', [Validators.required, Validators.pattern(/[\S]/)]],
      birthDate: ['', Validators.required],
      addressLine1: ['', [Validators.required, Validators.pattern(/[\S]/)]],
      addressLine2: [''],
      city: ['', [Validators.required, Validators.pattern(/[\S]/)]],
      email: ['', Validators.email],
      state: ['', Validators.required],
      country: [1, Validators.required],
      postalCode: ['', [Validators.required, Validators.pattern(/[\S]/)]],
      phoneNumber: ['', [Validators.required, Validators.pattern(/[\S]/)]],
      consent: [true, Validators.requiredTrue],
      middleNameNotGiven: [false, Validators.required]
    });
  }

  async onVerifyNowClick() {
    this.guestForm.patchValue({
      phoneNumber: this.getStrippedPhoneNumber()
    });
    
    this.sendGuestVerification();
  }

  middleNameToggled() {
    const middleNameControl = this.guestForm.get('middleName');
    const middleNameNotGivenControl = this.guestForm.get('middleNameNotGiven');
    
    if (!middleNameControl || !middleNameNotGivenControl) {
      return;
    }
    
    if (middleNameNotGivenControl.value) {
      middleNameControl.setValue('');
      middleNameControl.disable();
    } else { 
      middleNameControl.enable();
    };
  }

  async sendGuestVerification() {
    try {
      const guestVerificationRequest = Object.assign({}, ...Object
        .keys(this.guestForm.controls)
        .map(key => ({ [key]: this.guestForm.get(key)?.value }))
      );
      const response = await this.guestService.createGuestVerification(guestVerificationRequest);
      this.router.navigate([`/guests/detail/${response[0].guest_id}`]);
    } catch(e) {
      this.logService.handleError(e, 'An error occurred while requesting guest verification', 'An error occurred while requesting guest verification');
    }
  }

  onPhoneNumberChanged(event: Event) {
    this.updatePhoneNumber((event.target as HTMLInputElement).value);
  }

  updatePhoneNumber(phoneNumber: string) {
    const formattedPhoneNumber = formatPhoneNumber(phoneNumber, this.guestScreeningService.countryCode$.value as CountryCode);
    this.guestForm.patchValue({ phoneNumber: formattedPhoneNumber });
  }

  getStrippedPhoneNumber() {
    try {
      return formatNationalPhoneNumber(this.phoneNumber.value, this.guestScreeningService.countryCode$.value as CountryCode);
    } catch (e) {
      this.logService.handleError(e, `An error occured while parsing Guest Phone Number: ${this.phoneNumber.value}`, `Error while parsing Guest Phone Number: ${this.phoneNumber.value}`);
      return null;
    }
  }

  validatePhoneNumber(phoneNumberControl: FormControl) {
    try {
      if(!phoneNumberControl.value || !this.guestScreeningService.countryCode$.value) {
        return { invalid: true };
      }

      const country = this.guestScreeningService.countries$.value.find(c => c.id === this.guestForm.get('country')?.value);
      const isValid = validatePhoneNumber(phoneNumberControl.value, country?.code as CountryCode);
      return isValid ? null : { invalid: true };
    } catch (e) {
      this.logService.handleError(e, `An error occured while parsing Guest Phone Number: ${phoneNumberControl.value}`, `Error while parsing Guest Phone Number: ${this.phoneNumber.value}`);
      return { invalid: true };
    }
  }

  updatePhoneNumberWhenCountryCodeChanges() {
    if(!this.phoneNumber?.value) {
      return;
    }

    const phoneNumber = this.getStrippedPhoneNumber();
    this.updatePhoneNumber(phoneNumber ?? '');
    this.phoneNumber?.updateValueAndValidity();
  }

  navigateToDetailPage(guestId: string) {
    this.router.navigate([`/guests/detail/${guestId}`]);
  }

  get phoneNumber(): FormControl {
    return <FormControl>this.guestForm.get('phoneNumber');
  }
}